import React, { useEffect, useMemo } from 'react';
import {
  BundlesWrapper,
  IBundlesWrapperProps,
} from './components/Bundles/Wrapper';
import { BannerWrapper, IBannerWrapperProps } from './components/BannerWrapper';
import { MissingOffersBlock } from './components/MissingOffersBlock';
import {
  ISpecialOffersWrapperProps,
  SpecialOffersWrapper,
} from './components/SpecialOffers/Wrapper';
import {
  IRollingOffersWrapperProps,
  RollingOffersWrapper,
} from './components/RollingOffers/Wrapper';
import { StyledOffersWrapper } from './style';
import {
  EBundlesInternalViewModel,
  EFeatureFlag,
  EQueryParams,
  StoreBodyBlock,
} from 'utils/enums';
import {
  APIProps,
  BootResponse,
  IAppState,
  ICustomEvents,
  IPopupState,
  IProductResponse,
  IUseApi,
  OfferData,
} from 'utils/types';
import { useUrlQuery } from 'utils/index';
import { useStoreState } from 'state/hooks/store.state.hook';
import {
  IPlayerLevelWrapperProps,
  PlayerLevelWrapper,
} from './components/PlayerLevels/Wrapper';
import { PopupsWrapper } from './components/Popups/Wrapper';
import {
  DailyBonusWrapper,
  IDailyBonusWrapperProps,
} from './components/DailyBonus/Wrapper';

export interface IShopPageProps {
  useApi: (props: APIProps) => IUseApi;
  activeOrderId: string;
  isSSE?: boolean;
  appState: IAppState;
  popupState: IPopupState;
  customEvents: ICustomEvents;
  shouldUseSharedPopups?: boolean;
  t?: any;
  shouldDisplayPopups: boolean;
  selectOffer: (offer: OfferData) => void;
  hasNoOffers: boolean; // TODO: can be moved to shared-ui
  tooltipOpenEventHandler?: () => void;
  tooltipLoadCallback?: (arr: string[]) => Promise<IProductResponse[]>;
  refetchOffers: () => void; // todo: remove and use useApi instead
  sendEvent: (event: OfferData) => void;
}

const DEFAULT_BODY_BLOCKS_ORDER: StoreBodyBlock[] = [
  StoreBodyBlock.dailyBonus,
  StoreBodyBlock.playerLevel,
  StoreBodyBlock.banners,
  StoreBodyBlock.specialOffers,
  StoreBodyBlock.rollingOffers,
  StoreBodyBlock.bundles,
];

const mapBlocksToComponents: Partial<Record<StoreBodyBlock, React.FC<any>>> = {
  [StoreBodyBlock.dailyBonus]: DailyBonusWrapper,
  [StoreBodyBlock.playerLevel]: PlayerLevelWrapper,
  [StoreBodyBlock.banners]: BannerWrapper,
  [StoreBodyBlock.specialOffers]: SpecialOffersWrapper,
  [StoreBodyBlock.rollingOffers]: RollingOffersWrapper,
  [StoreBodyBlock.bundles]: BundlesWrapper,
};

export const StoreLayout: React.FC<IShopPageProps> = ({
  useApi,
  activeOrderId,
  isSSE = false,
  appState,
  popupState,
  customEvents,
  shouldUseSharedPopups = false,
  shouldDisplayPopups,
  selectOffer,
  hasNoOffers,
  t,
  tooltipOpenEventHandler,
  tooltipLoadCallback,
  refetchOffers,
  sendEvent,
}) => {
  const { storeTheme, setSendEvent, setStoreTheme } = useStoreState();
  const campaignOfferId = useUrlQuery(EQueryParams.OFFER_ID);

  const API = useApi({ orderId: activeOrderId, isSSE: isSSE || false });

  const bootData = API.getPublisherMeta?.data as BootResponse;

  const offersData = API.getOffers?.data;

  useEffect(() => {
    if (!sendEvent) return;

    setSendEvent(sendEvent);
  }, [sendEvent]);

  useEffect(() => {
    setStoreTheme(bootData?.storeTheme);
  }, [bootData?.storeTheme]);

  const bundlesInternalViewModel = useMemo(
    () => storeTheme?.general?.bundlesInternalViewModel,
    [storeTheme?.general?.bundlesInternalViewModel],
  );

  const bodyBlocksOrder = useMemo(() => {
    const themeBodyBlocks = storeTheme?.storeTemplate?.bodyBlocksOrder;

    return themeBodyBlocks?.length
      ? themeBodyBlocks
      : DEFAULT_BODY_BLOCKS_ORDER;
  }, [storeTheme?.storeTemplate?.bodyBlocksOrder]);

  const bannerProps: IBannerWrapperProps = useMemo(
    () => ({
      noOffers: Boolean(hasNoOffers || !offersData || campaignOfferId),
      bundlesInternalViewModel: bundlesInternalViewModel,
      bannerImage:
        offersData?.bannerImage || storeTheme?.storeScreen?.defaultBanner,
      playerLevelImg: offersData?.playerLevelImg,
    }),
    [
      storeTheme?.storeScreen?.defaultBanner,
      offersData,
      hasNoOffers,
      campaignOfferId,
      bundlesInternalViewModel,
    ],
  );

  const bundleWrapperProps: IBundlesWrapperProps = useMemo(
    () => ({
      selectOffer,
      sections: offersData?.sections,
      bundles: offersData?.offers?.bundles,
      bundleIdFocus: offersData?.offerFocus?.bundleId,
      tooltipOpenEventHandler,
      tooltipLoadCallback,
      t,
    }),
    [
      selectOffer,
      tooltipOpenEventHandler,
      tooltipLoadCallback,
      offersData?.sections,
      offersData?.offers?.bundles,
      offersData?.offerFocus?.bundleId,
      t,
    ],
  );

  const specialOffersWrapperProps: ISpecialOffersWrapperProps = useMemo(
    () => ({
      selectOffer,
      specialOffers: offersData?.offers?.promotions,
      tooltipOpenEventHandler,
      refetchOffers,
      t,
    }),
    [
      selectOffer,
      tooltipOpenEventHandler,
      refetchOffers,
      offersData?.offers?.promotions,
      t,
    ],
  );

  const playerLevels = useMemo(
    () =>
      offersData?.playerLevel
        ? [offersData?.playerLevel]
        : offersData?.playerLevels ?? [],
    [offersData?.playerLevel, offersData?.playerLevels],
  );

  const playerLevelWrapperProps: IPlayerLevelWrapperProps = useMemo(
    () => ({
      hasOffers: offersData?.offers,
      playerLevels,
      playerLevelImage: offersData?.playerLevelImg,
      refetchOffers,
      t,
    }),
    [
      refetchOffers,
      playerLevels,
      offersData?.offers,
      offersData?.playerLevelImg,
      t,
    ],
  );

  const dailyBonusWrapperProps: IDailyBonusWrapperProps = useMemo(
    () => ({
      useApi,
      popupState,
      t,
    }),
    [useApi, popupState, t],
  );

  const rollingOffersWrapperProps: IRollingOffersWrapperProps = useMemo(
    () => ({
      rollingOffers: offersData?.offers?.rolling,
      refetchOffers,
      selectOffer,
      isHorizontalViewEnabled:
        bootData?.featureFlags[EFeatureFlag.STORE_ROLLING_HORIZONTAL_VIEW],
    }),
    [
      refetchOffers,
      selectOffer,
      offersData?.offers?.rolling,
      bootData?.featureFlags,
    ],
  );

  const mapBlocksToProps: Partial<Record<StoreBodyBlock, Object>> = useMemo(
    () => ({
      [StoreBodyBlock.dailyBonus]: dailyBonusWrapperProps,
      [StoreBodyBlock.playerLevel]: playerLevelWrapperProps,
      [StoreBodyBlock.banners]: bannerProps,
      [StoreBodyBlock.specialOffers]: specialOffersWrapperProps,
      [StoreBodyBlock.rollingOffers]: rollingOffersWrapperProps,
      [StoreBodyBlock.bundles]: bundleWrapperProps,
    }),
    [
      playerLevelWrapperProps,
      bannerProps,
      specialOffersWrapperProps,
      rollingOffersWrapperProps,
      bundleWrapperProps,
      dailyBonusWrapperProps,
    ],
  );

  if (!storeTheme || !bootData) return <></>;

  return (
    <StyledOffersWrapper className="store-layout" gap={1}>
      {shouldUseSharedPopups && (
        <PopupsWrapper
          useApi={useApi}
          popupState={popupState}
          appState={appState}
          customEvents={customEvents}
          selectOffer={selectOffer}
          shouldDisplayPopups={shouldDisplayPopups}
          t={t}
        />
      )}
      {bodyBlocksOrder.map((key: StoreBodyBlock) => {
        const Block: React.FC | undefined = mapBlocksToComponents[key];
        if (Block) {
          return <Block key={key} {...mapBlocksToProps[key]} />;
        }
      })}

      <MissingOffersBlock hasNoOffers={hasNoOffers} t={t} />

      {(bundlesInternalViewModel === EBundlesInternalViewModel.BAGELCODE ||
        bundlesInternalViewModel === EBundlesInternalViewModel.BAGELCODE2) &&
        offersData?.playerLevelImg && (
          <div className="player-level-image">
            <img src={storeTheme!.general.logo} alt="logo" />
            <img alt="Player Level" src={offersData?.playerLevelImg} />
          </div>
        )}
    </StyledOffersWrapper>
  );
};
